import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import agentsService, { Agent } from '../services/agentsService';
import { Spinner } from './ui';

/**
 * Gate an agent-scoped view on the caller's permitted agents (from GET /agents). If the user
 * lacks access to `agentKey`, a clean "no access" panel is shown instead of the content — so a
 * deep-link to an agent the user can't use never leaks that agent's data. The real enforcement
 * is server-side; this is the matching client affordance.
 */
const RequireAgent: React.FC<{ agentKey: string; children: React.ReactNode }> = ({ agentKey, children }) => {
  const [state, setState] = useState<'loading' | 'ok' | 'denied'>('loading');

  useEffect(() => {
    let alive = true;
    agentsService.listMyAgents()
      .then(({ agents }) => {
        if (!alive) return;
        setState(agents.some((a: Agent) => a.key === agentKey) ? 'ok' : 'denied');
      })
      .catch(() => alive && setState('denied'));
    return () => { alive = false; };
  }, [agentKey]);

  if (state === 'loading') {
    return <div className="mx-card mx-card-pad"><Spinner label="Checking access…" /></div>;
  }
  if (state === 'denied') {
    return (
      <div className="mx-card mx-card-pad" style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <Lock size={16} /> No access to this agent
        </div>
        <div className="mx-muted" style={{ fontSize: 13, marginTop: 8 }}>
          You don’t have permission to use the “{agentKey}” agent. Ask an administrator to grant
          you access.
        </div>
        <Link to="/admin/docshawk" className="mx-subtle" style={{ fontSize: 12.5, marginTop: 12, display: 'inline-block' }}>
          ← Back to your workspace
        </Link>
      </div>
    );
  }
  return <>{children}</>;
};

export default RequireAgent;
